'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useRef, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { api, ApiError, useAuth } from '@splace/core';
const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? '';

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        retry: (count, err: any) => {
          if (err?.status === 401 || err?.status === 403) return false;
          return count < 2;
        },
      },
    },
  });
}

export function QueryProvider({ children }: { children: ReactNode }) {
  const clientRef = useRef<QueryClient | null>(null);
  if (!clientRef.current) clientRef.current = makeQueryClient();

  const setUser = useAuth((s) => s.setUser);
  const setLoading = useAuth((s) => s.setLoading);
  const clear = useAuth((s) => s.clear);
  const router = useRouter();

  // Bootstrap: ask the backend who we are. If the splace_session cookie is
  // valid the request returns the user; on 401 the cookie is stale (revoked
  // session, deleted user, expired/rotated JWT) so we ask the backend to
  // clear it and call router.refresh() — that re-runs the middleware on the
  // current URL, which now sees no cookie and redirects protected pages to
  // /login. Without the refresh the user would have to manually reload twice
  // to get unstuck on the dead session.
  //
  // Tracking `isLoading` here is what stops the protected layout from firing
  // its `router.replace('/login?next=…')` during the bootstrap window — the
  // proxy would otherwise bounce `/login` → `/feed` for users with a valid
  // cookie and silently swallow the `next` param.
  useEffect(() => {
    api.users.me()
      .then((user) => setUser({
        id: user.id,
        email: user.email,
        username: user.username,
        displayName: user.displayName,
        bio: user.bio,
        photoUrl: user.photoUrl,
        photoThumbUrl: user.photoThumbUrl,
        countLinks: user.countLinks,
        createdAt: user.createdAt,
      }))
      .catch(async (err) => {
        if (err instanceof ApiError && err.status === 401) {
          await api.auth.logout().catch(() => { /* ignore */ });
          clear();
          router.refresh();
        }
      })
      .finally(() => setLoading(false));
  }, [setUser, setLoading, clear, router]);

  const inner = (
    <QueryClientProvider client={clientRef.current}>
      {children}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );

  // Only wrap when a Google client ID is configured — otherwise the provider
  // throws and the rest of the app can't render. Components that show the
  // Google button check the same env var and degrade gracefully.
  return GOOGLE_CLIENT_ID ? (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{inner}</GoogleOAuthProvider>
  ) : (
    inner
  );
}
